/**
 * arloHydration.js - Resolve Arlo content_ids that aren't in the homepage payload
 *
 * Arlo recommends across the full catalog; the homepage payload only carries the
 * channels on its own rails, so anything else must be hydrated one channel at a
 * time (there is no batch endpoint). Bounded so a pathological response can't
 * fan out into dozens of requests; getChannelBySlug/ById cache per channel.
 * Shared by useArloRecommendations (rails) and useAskArlo (chat picks).
 */
import spotlightService from '../services/SpotlightService';

// Worst case: 8 rails (server MAX_BLOCKS) × ~12 items, all off-homepage.
// Requests dedupe via SpotlightService's per-channel cache + pending-request
// map, and dev/prod are HTTP/2, so the burst multiplexes over one connection.
export const MAX_HYDRATIONS = 96;

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

const pickChannel = response => {
  if (!response) {return null;}
  if (Array.isArray(response.channels)) {return response.channels[0] || null;}
  return response._id ? response : null;
};

/**
 * Fetch full channel objects for the given content_ids (slugs or ObjectIds).
 * Returns a Map of content_id -> channel; ids that fail to hydrate are simply
 * absent (fail-silent per item, warned in aggregate). Never throws.
 */
export const hydrateMissingChannels = async contentIds => {
  const unique = [...new Set(contentIds)].slice(0, MAX_HYDRATIONS);
  if (contentIds.length > unique.length && unique.length === MAX_HYDRATIONS) {
    console.warn(
      `arloHydration: hydration capped at ${MAX_HYDRATIONS} of ${new Set(contentIds).size} missing channels`,
    );
  }
  const results = await Promise.allSettled(
    unique.map(id =>
      OBJECT_ID_RE.test(id)
        ? spotlightService.getChannelById(id)
        : spotlightService.getChannelBySlug(id),
    ),
  );
  const hydrated = new Map();
  const failed = [];
  results.forEach((result, i) => {
    const channel = result.status === 'fulfilled' ? pickChannel(result.value) : null;
    if (channel && channel._id) {
      hydrated.set(unique[i], channel);
    } else {
      failed.push(
        `${unique[i]} (${result.status === 'rejected' ? result.reason?.message || 'rejected' : 'no channel in response'})`,
      );
    }
  });
  if (failed.length > 0) {
    console.warn(
      `arloHydration: ${failed.length}/${unique.length} hydrations failed:`,
      failed.join('; '),
    );
  }
  return hydrated;
};
